import { InjectionToken, Provider } from '@angular/core';
import { isObservable, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { DeviceUiFeatureId } from './device-ui-feature-toggle.models';

/**
 * Configuration object for device-ui feature toggling.
 *
 * Contains an observable of disabled feature IDs that can resolve synchronously
 * (static array) or asynchronously (e.g. after Statsig SDK initialization).
 * Features not in the set are enabled by default.
 */
export interface DeviceUiFeatureToggleConfig {
	readonly disabledFeatures$: Observable<ReadonlySet<DeviceUiFeatureId>>;
}

/** Default config with no features disabled (all visible). */
export const DEFAULT_DEVICE_UI_FEATURE_TOGGLE_CONFIG: DeviceUiFeatureToggleConfig = {
	disabledFeatures$: of(new Set<DeviceUiFeatureId>())
};

/**
 * Injection token for device-ui feature toggle configuration.
 *
 * When not provided by a host application, defaults to all features enabled.
 */
export const DEVICE_UI_FEATURE_TOGGLE_TOKEN = new InjectionToken<DeviceUiFeatureToggleConfig>(
	'DEVICE_UI_FEATURE_TOGGLE_TOKEN',
	{
		providedIn: 'root',
		factory: (): DeviceUiFeatureToggleConfig => DEFAULT_DEVICE_UI_FEATURE_TOGGLE_CONFIG
	}
);

/**
 * Provider helper for host applications to disable specific device-ui features.
 *
 * Accepts either a static array or an `Observable` that resolves the disabled
 * feature list asynchronously (e.g. after Statsig SDK init).
 * Features not listed remain enabled (visible) by default.
 * TypeScript enforces the `DeviceUiFeatureId` type at compile time.
 *
 * Hierarchical IDs are supported: disabling `'settings.wireless-mics'` also
 * implicitly disables `'settings.wireless-mics.rf-spectrum-scanner'`.
 *
 * @example
 * ```typescript
 * // Static configuration — disable specific sections
 * provideDisabledDeviceUiFeatures(['settings.ip-configuration', 'settings.wireless-mics'])
 *
 * // Reactive configuration — resolve from Statsig after SDK init
 * provideDisabledDeviceUiFeatures(
 *   statsigService.isLoading$.pipe(
 *     filter(loading => !loading),
 *     map(() => resolveDisabledFeatures(statsigService))
 *   )
 * )
 * ```
 */
export function provideDisabledDeviceUiFeatures(
	disabledFeatures: DeviceUiFeatureId[] | Observable<DeviceUiFeatureId[]>
): Provider {
	let disabledFeatures$: Observable<ReadonlySet<DeviceUiFeatureId>>;

	if (isObservable(disabledFeatures)) {
		disabledFeatures$ = disabledFeatures.pipe(map((ids) => new Set(ids)));
	} else {
		disabledFeatures$ = of(new Set(disabledFeatures));
	}

	return {
		provide: DEVICE_UI_FEATURE_TOGGLE_TOKEN,
		useValue: { disabledFeatures$ } satisfies DeviceUiFeatureToggleConfig
	};
}
